/* =============================================================================
 * AYA Toast — H_UIPOLISH_V1_L3 Palier 1
 * -----------------------------------------------------------------------------
 * Toast léger autonome (sans dépendance à Sonner/Radix existants).
 * - 4 variants : success | error | info | warning
 * - Position top-right, slide-in spring (framer-motion via LazyMotion)
 * - Auto-dismiss 4s (overridable via prop duration)
 * - Reduced-motion : la prop respect des défauts framer-motion s'applique
 *   automatiquement (cf. useReducedMotion).
 *
 * Usage:
 *   import { AyaToastProvider, useAyaToast } from "./Toast";
 *   <AyaToastProvider>{app}</AyaToastProvider>
 *   const toast = useAyaToast();
 *   toast.show({ variant: "success", title: "OK", description: "…" });
 *
 * Aucune intégration globale au montage de l'app dans ce palier — composant
 * "disponible en bibliothèque". L'intégration effective sera faite en Palier 2.
 * ========================================================================== */

import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    useEffect,
} from "react";
import {
    LazyMotion,
    domAnimation,
    m,
    AnimatePresence,
    useReducedMotion,
} from "framer-motion";

const AyaToastContext = createContext(null);

const VARIANT_STYLES = {
    success: {
        bg: "var(--aya-status-success-soft)",
        border: "var(--aya-status-success-border)",
        accent: "var(--aya-status-success)",
        icon: "✓",
    },
    error: {
        bg: "var(--aya-status-error-soft)",
        border: "var(--aya-status-error-border)",
        accent: "var(--aya-status-error)",
        icon: "✕",
    },
    info: {
        bg: "var(--aya-status-info-soft)",
        border: "var(--aya-status-info-border)",
        accent: "var(--aya-status-info)",
        icon: "ℹ",
    },
    warning: {
        bg: "var(--aya-status-warning-soft)",
        border: "var(--aya-status-warning-border)",
        accent: "var(--aya-status-warning)",
        icon: "!",
    },
};

const DEFAULT_DURATION = 4000;

function ToastItem({ toast, onDismiss }) {
    const reduce = useReducedMotion();
    const style = VARIANT_STYLES[toast.variant] || VARIANT_STYLES.info;

    useEffect(() => {
        if (toast.duration === Infinity) return undefined;
        const t = setTimeout(
            () => onDismiss(toast.id),
            toast.duration ?? DEFAULT_DURATION
        );
        return () => clearTimeout(t);
    }, [toast.id, toast.duration, onDismiss]);

    return (
        <m.div
            data-testid={`aya-toast-${toast.variant}`}
            role="status"
            aria-live={toast.variant === "error" ? "assertive" : "polite"}
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: 24, scale: 0.96 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, x: 24, scale: 0.98 }}
            transition={
                reduce
                    ? { duration: 0.16 }
                    : { type: "spring", stiffness: 320, damping: 28, mass: 0.6 }
            }
            style={{
                background: style.bg,
                border: `1px solid ${style.border}`,
                borderLeft: `3px solid ${style.accent}`,
                borderRadius: "var(--aya-radius-md)",
                boxShadow: "var(--aya-shadow-lg)",
                padding: "var(--aya-space-3) var(--aya-space-4)",
                color: "var(--aya-text-primary)",
                display: "flex",
                alignItems: "flex-start",
                gap: "var(--aya-space-3)",
                minWidth: "260px",
                maxWidth: "360px",
                backdropFilter: `blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))`,
                WebkitBackdropFilter: `blur(var(--aya-glass-blur-sm)) saturate(var(--aya-glass-saturate))`,
                pointerEvents: "auto",
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    flex: "0 0 auto",
                    width: 22,
                    height: 22,
                    borderRadius: "var(--aya-radius-full)",
                    background: style.accent,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                }}
            >
                {style.icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                {toast.title ? (
                    <div
                        style={{
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.3,
                            color: "var(--aya-text-primary)",
                        }}
                    >
                        {toast.title}
                    </div>
                ) : null}
                {toast.description ? (
                    <div
                        style={{
                            marginTop: toast.title ? 2 : 0,
                            fontSize: 13,
                            lineHeight: 1.4,
                            color: "var(--aya-text-secondary)",
                        }}
                    >
                        {toast.description}
                    </div>
                ) : null}
            </div>
            <button
                type="button"
                data-testid="aya-toast-close"
                onClick={() => onDismiss(toast.id)}
                aria-label="Fermer la notification"
                style={{
                    flex: "0 0 auto",
                    border: "none",
                    background: "transparent",
                    color: "var(--aya-text-muted)",
                    cursor: "pointer",
                    padding: 2,
                    borderRadius: "var(--aya-radius-xs)",
                    fontSize: 16,
                    lineHeight: 1,
                }}
            >
                ×
            </button>
        </m.div>
    );
}

export function AyaToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback((opts = {}) => {
        const id = ++idRef.current;
        const next = {
            id,
            variant: opts.variant || "info",
            title: opts.title || null,
            description: opts.description || null,
            duration: opts.duration ?? DEFAULT_DURATION,
        };
        setToasts((prev) => [...prev, next]);
        return id;
    }, []);

    const api = useMemo(
        () => ({
            show,
            dismiss,
            success: (o) => show({ ...o, variant: "success" }),
            error: (o) => show({ ...o, variant: "error" }),
            info: (o) => show({ ...o, variant: "info" }),
            warning: (o) => show({ ...o, variant: "warning" }),
        }),
        [show, dismiss]
    );

    return (
        <AyaToastContext.Provider value={api}>
            {children}
            <LazyMotion features={domAnimation} strict>
                <div
                    data-testid="aya-toast-viewport"
                    aria-live="polite"
                    style={{
                        position: "fixed",
                        top: "var(--aya-space-4)",
                        right: "var(--aya-space-4)",
                        display: "flex",
                        flexDirection: "column",
                        gap: "var(--aya-space-2)",
                        zIndex: "var(--aya-z-toast)",
                        pointerEvents: "none",
                    }}
                >
                    <AnimatePresence initial={false}>
                        {toasts.map((t) => (
                            <ToastItem
                                key={t.id}
                                toast={t}
                                onDismiss={dismiss}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            </LazyMotion>
        </AyaToastContext.Provider>
    );
}

export function useAyaToast() {
    const ctx = useContext(AyaToastContext);
    if (!ctx) {
        // Fallback inerte : permet d'importer le hook sans provider monté
        // (utile pendant le Palier 1 où le Provider n'est pas encore wired).
        return {
            show: () => null,
            dismiss: () => null,
            success: () => null,
            error: () => null,
            info: () => null,
            warning: () => null,
        };
    }
    return ctx;
}

export default AyaToastProvider;
